import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import NavButton from "./NavButton";
import Grow from "@mui/material/Grow";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import { useState, useRef, useLayoutEffect, useEffect } from "react";
import NotificationMenu from "./components/NotificationMenu";
import isAuthenticated from "../Utils/isAuthenticated";
import GiveRoleForm from "../../pages/Access/GiveRoleForm";
import { fetchResultsEmailNotifications } from "../Api/Getters";
import { useQuery } from "react-query";
import LaunchIcon from "@mui/icons-material/Launch";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useLocation } from "react-router";
import { Link as RouterLink } from "react-router-dom";
import {
	NAV_GROUPS,
	canSeeNavGroup,
	getNavGroupRoutes,
	getNavGroupExternalLinks,
} from "../../config/routes";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

function AppBarNav() {
	const auth = isAuthenticated();
	const adminUser = Boolean(auth.access && auth.access.includes("ADMIN"));
	const ccid = auth.ccid;
	const token = auth.token;
	const [mouseOverButtonState, setMouseOverButtonState] = useState(""); //default ""
	const [maxHeight, setMaxHeight] = useState(0); // default 0
	const reportingRefX = useRef(null);
	const spotRefX = useRef(null);
	const utilitiesRefX = useRef(null);
	const adminRefX = useRef(null);
	const examRefX = useRef(null);
	const [widthReporting, setWidthReporting] = useState(0);
	const [widthSPOT, setWidthSPOT] = useState(0);
	const [widthUtilities, setWidthUtilities] = useState(0);
	const [widthAdmin, setWidthAdmin] = useState(0);
	const [widthExam, setWidthExam] = useState(0);

	// Mobile drawer state
	const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
	const [mobileExpandedGroup, setMobileExpandedGroup] = useState("");

	const location = useLocation();
	const anchors = {
		reporting: reportingRefX,
		SPOT: spotRefX,
		utilities: utilitiesRefX,
		admin: adminRefX,
		exam: examRefX,
	};

	const anchorEl = anchors[mouseOverButtonState]?.current;

	// keep menu open while mouse is over the button *or* the popper
	const [open, setOpen] = useState(false);
	const closeDelayRef = useRef(null);

	const openMenu = (name) => {
		calculateHeight(name);
		if (closeDelayRef.current) {
			window.clearTimeout(closeDelayRef.current);
			closeDelayRef.current = null;
		}
		setMouseOverButtonState(name);
		setOpen(true);
	};

	const scheduleClose = () => {
		// tiny delay to allow moving the cursor from button to menu without flicker
		closeDelayRef.current = window.setTimeout(() => setOpen(false), 120);
	};

	const calculateHeight = (buttonName) => {
		// Map button names to nav group names
		const groupMap = {
			reporting: "reporting",
			SPOT: "spot",
			exam: "exam",
			utilities: "utilities",
			admin: "admin",
		};
		const groupName = groupMap[buttonName];

		// Get routes and external links for this group that user can access
		const routes = getNavGroupRoutes(groupName, userAccess);
		const externalLinks = getNavGroupExternalLinks(groupName, userAccess);

		// Calculate the maximum height based on the number of items
		const totalItems = routes.length + externalLinks.length;
		const newMaxHeight = 20 + totalItems * 45;
		setMaxHeight(newMaxHeight);
	};

	const handlePopperEnter = () => {
		if (closeDelayRef.current) {
			window.clearTimeout(closeDelayRef.current);
			closeDelayRef.current = null;
		}
	};
	const handlePopperLeave = () => scheduleClose();

	// Mobile drawer handlers
	const toggleMobileDrawer = () => {
		setMobileDrawerOpen(!mobileDrawerOpen);
	};

	const handleMobileGroupClick = (groupName) => {
		setMobileExpandedGroup(mobileExpandedGroup === groupName ? "" : groupName);
	};

	const handleMobileNavClick = () => {
		setMobileDrawerOpen(false);
		setMobileExpandedGroup("");
	};

	const widthByKey = {
		reporting: widthReporting,
		SPOT: widthSPOT,
		utilities: widthUtilities,
		admin: widthAdmin,
		exam: widthExam,
	};
	const [resultsEmailNotificationData, setResultsEmailNotificationData] = useState([]);

	const fetchNotifications = async () => {
		const resultsEmailNotifications = await fetchResultsEmailNotifications(token);
		return resultsEmailNotifications; // ✅ return the actual data, not an object wrapper
	};

	const { data } = useQuery({
		queryKey: ["notifications"], // ✅ use array form for clarity and caching
		queryFn: fetchNotifications, // ✅ queryFn must return data directly
		enabled: !!adminUser, // ✅ ensure it's truthy before running
		staleTime: 1000 * 30, // ✅ 30 seconds
		refetchInterval: 1000 * 60 * 2, // (optional) if you want it to run every 2 min
	});

	useEffect(() => {
		if (data) {
			// if you really need to sync to local state for some reason:
			setResultsEmailNotificationData(data);
		}
	}, [data]);

	const access = auth.access;

	// Parse access string into array for helper functions
	const userAccess = access
		? access
				.split(",")
				.map((a) => a.trim())
				.filter(Boolean)
		: [];

	useLayoutEffect(() => {
		if (location.pathname === "/sso/callback") {
			return;
		}
		if (canSeeNavGroup("reporting", userAccess)) {
			setWidthReporting(reportingRefX.current.offsetWidth);
		}
		if (canSeeNavGroup("admin", userAccess)) {
			setWidthAdmin(adminRefX.current.offsetWidth);
		}
		if (canSeeNavGroup("spot", userAccess)) {
			setWidthSPOT(spotRefX.current.offsetWidth);
		}
		if (canSeeNavGroup("utilities", userAccess)) {
			setWidthUtilities(utilitiesRefX.current.offsetWidth);
		}
		if (canSeeNavGroup("exam", userAccess)) {
			setWidthExam(examRefX.current.offsetWidth);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [access, location.pathname]);

	if (auth.access && !location.pathname.includes("/sso/callback")) {
		return (
			<AppBar
				position="static"
				color="primary"
				sx={{
					width: "100%",
					zIndex: 11,
					boxShadow: "none",
					bgcolor: "#F5F5F5",

					//boxShadow: location.pathname.includes("/sso/callback") && "none",
				}}
			>
				<Toolbar
					sx={{
						display: "flex",
						flexDirection: "row",
					}}
				>
					{/* Mobile Hamburger Menu Button */}
					<IconButton
						color="primary"
						aria-label="open navigation menu"
						edge="start"
						onClick={toggleMobileDrawer}
						sx={{ display: { xs: "flex", md: "none" }, mr: 1 }}
					>
						<MenuIcon sx={{ color: "primary", fontSize: 28 }} />
					</IconButton>

					{/* Mobile Drawer */}
					<Drawer
						anchor="left"
						open={mobileDrawerOpen}
						onClose={toggleMobileDrawer}
						sx={{ display: { xs: "block", md: "none" } }}
					>
						<Box sx={{ width: 400 }} role="presentation">
							<List>
								{canSeeNavGroup("reporting", userAccess) && (
									<>
										<ListItemButton onClick={() => handleMobileGroupClick("reporting")}>
											<ListItemText
												primary={NAV_GROUPS.reporting.label}
												primaryTypographyProps={{ fontWeight: 600 }}
											/>
											{mobileExpandedGroup === "reporting" ? <ExpandLess /> : <ExpandMore />}
										</ListItemButton>
										<Collapse in={mobileExpandedGroup === "reporting"} timeout="auto" unmountOnExit>
											<List component="div" disablePadding>
												{getNavGroupRoutes("reporting", userAccess).map((route) => (
													<ListItemButton
														key={route.path}
														component={RouterLink}
														to={route.path}
														onClick={handleMobileNavClick}
														sx={{ pl: 4 }}
													>
														<ListItemText primary={route.label} />
													</ListItemButton>
												))}
											</List>
										</Collapse>
										<Divider />
									</>
								)}

								{canSeeNavGroup("spot", userAccess) && (
									<>
										<ListItemButton onClick={() => handleMobileGroupClick("spot")}>
											<ListItemText
												primary={NAV_GROUPS.spot.label}
												primaryTypographyProps={{ fontWeight: 600 }}
											/>
											{mobileExpandedGroup === "spot" ? <ExpandLess /> : <ExpandMore />}
										</ListItemButton>
										<Collapse in={mobileExpandedGroup === "spot"} timeout="auto" unmountOnExit>
											<List component="div" disablePadding>
												{getNavGroupRoutes("spot", userAccess).map((route) => (
													<ListItemButton
														key={route.path}
														component={RouterLink}
														to={route.path}
														onClick={handleMobileNavClick}
														sx={{ pl: 4 }}
													>
														<ListItemText primary={route.label} />
													</ListItemButton>
												))}
												{getNavGroupExternalLinks("spot", userAccess).map((link) => (
													<ListItemButton
														key={link.url}
														component="a"
														href={link.url}
														target="_blank"
														rel="noopener noreferrer"
														onClick={handleMobileNavClick}
														sx={{ pl: 4 }}
													>
														<ListItemText primary={link.label} />
														<LaunchIcon fontSize="small" />
													</ListItemButton>
												))}
											</List>
										</Collapse>
										<Divider />
									</>
								)}

								{canSeeNavGroup("exam", userAccess) && (
									<>
										{ccid !== "opscan" ? (
											<>
												<ListItemButton onClick={() => handleMobileGroupClick("exam")}>
													<ListItemText
														primary={NAV_GROUPS.exam.label}
														primaryTypographyProps={{ fontWeight: 600 }}
													/>
													{mobileExpandedGroup === "exam" ? <ExpandLess /> : <ExpandMore />}
												</ListItemButton>
												<Collapse in={mobileExpandedGroup === "exam"} timeout="auto" unmountOnExit>
													<List component="div" disablePadding>
														{getNavGroupRoutes("exam", userAccess).map((route) => (
															<ListItemButton
																key={route.path}
																component={RouterLink}
																to={route.path}
																onClick={handleMobileNavClick}
																sx={{ pl: 4 }}
															>
																<ListItemText primary={route.label} />
															</ListItemButton>
														))}
													</List>
												</Collapse>
											</>
										) : (
											<ListItemButton
												component={RouterLink}
												to="/test-scoring/request-for-service"
												onClick={handleMobileNavClick}
											>
												<ListItemText
													primary="Request Scanning"
													primaryTypographyProps={{ fontWeight: 600 }}
												/>
											</ListItemButton>
										)}
										<Divider />
									</>
								)}

								{canSeeNavGroup("utilities", userAccess) && (
									<>
										<ListItemButton onClick={() => handleMobileGroupClick("utilities")}>
											<ListItemText
												primary={NAV_GROUPS.utilities.label}
												primaryTypographyProps={{ fontWeight: 600 }}
											/>
											{mobileExpandedGroup === "utilities" ? <ExpandLess /> : <ExpandMore />}
										</ListItemButton>
										<Collapse in={mobileExpandedGroup === "utilities"} timeout="auto" unmountOnExit>
											<List component="div" disablePadding>
												{getNavGroupRoutes("utilities", userAccess).map((route) => (
													<ListItemButton
														key={route.path}
														component={RouterLink}
														to={route.path}
														onClick={handleMobileNavClick}
														sx={{ pl: 4 }}
													>
														<ListItemText primary={route.label} />
													</ListItemButton>
												))}
											</List>
										</Collapse>
										<Divider />
									</>
								)}

								{canSeeNavGroup("admin", userAccess) && (
									<>
										<ListItemButton onClick={() => handleMobileGroupClick("admin")}>
											<ListItemText
												primary={NAV_GROUPS.admin.label}
												primaryTypographyProps={{ fontWeight: 600 }}
											/>
											{mobileExpandedGroup === "admin" ? <ExpandLess /> : <ExpandMore />}
										</ListItemButton>
										<Collapse in={mobileExpandedGroup === "admin"} timeout="auto" unmountOnExit>
											<List component="div" disablePadding>
												{getNavGroupRoutes("admin", userAccess).map((route) => (
													<ListItemButton
														key={route.path}
														component={RouterLink}
														to={route.path}
														onClick={handleMobileNavClick}
														sx={{ pl: 4 }}
													>
														<ListItemText primary={route.label} />
													</ListItemButton>
												))}
											</List>
										</Collapse>
										<Divider />
									</>
								)}

								{adminUser && (
									<ListItemButton
										component={RouterLink}
										to="/admin/scanning-calendar"
										onClick={handleMobileNavClick}
									>
										<CalendarMonthIcon sx={{ mr: 1 }} />
										<ListItemText primary="Scanning Calendar" />
									</ListItemButton>
								)}

								{(userAccess.length === 0 ||
									!userAccess ||
									(userAccess.length === 1 && access === "STD")) && (
									<ListItem>
										<GiveRoleForm
											buttonVariant="outlined"
											buttonTitle="Need to Submit a Request for Service?"
											buttonWidth="100%"
										/>
									</ListItem>
								)}
							</List>
						</Box>
					</Drawer>
					<div className="flex items-center w-full justify-between">
						<div className="hidden md:flex md:flex-nowrap items-center justify-between gap-2 lg:gap-4 xl:gap-8 pl-0 md:pl-4">
							{canSeeNavGroup("reporting", userAccess) && (
								<RouterLink
									ref={reportingRefX}
									onMouseEnter={() => openMenu("reporting")}
									onMouseLeave={scheduleClose}
									to="/reporting"
									onClick={() => setOpen(false)}
									className={`px-4 py-2 text-xl font-normal text-gray-800 hover:bg-gray-100 rounded-md transition-colors no-underline ${
										location.pathname === "/reporting"
											? "bg-gray-100"
											: location.pathname.startsWith("/reporting")
												? "underline"
												: ""
									}`}
								>
									{NAV_GROUPS.reporting.label}
								</RouterLink>
							)}
							{canSeeNavGroup("spot", userAccess) && (
								<RouterLink
									ref={spotRefX}
									onMouseEnter={() => openMenu("SPOT")}
									onMouseLeave={scheduleClose}
									to="/spot"
									onClick={() => setOpen(false)}
									className={`px-4 py-2 text-xl font-normal text-gray-800 hover:bg-gray-100 rounded-md transition-colors no-underline ${
										location.pathname === "/spot"
											? "bg-gray-100"
											: location.pathname.startsWith("/spot")
												? "underline"
												: ""
									}`}
								>
									{NAV_GROUPS.spot.label}
								</RouterLink>
							)}

							{canSeeNavGroup("exam", userAccess) &&
								(ccid !== "opscan" ? (
									<RouterLink
										ref={examRefX}
										onMouseEnter={() => openMenu("exam")}
										to="/test-scoring"
										onClick={() => setOpen(false)}
										onMouseLeave={scheduleClose}
										className={`px-4 py-2 text-xl font-normal text-gray-800 hover:bg-gray-100 rounded-md transition-colors whitespace-nowrap no-underline ${
											location.pathname === "/test-scoring"
												? "bg-gray-100"
												: location.pathname.startsWith("/test-scoring")
													? "underline"
													: ""
										}`}
									>
										{NAV_GROUPS.exam.label}
									</RouterLink>
								) : (
									<RouterLink
										ref={examRefX}
										to="/test-scoring/request-for-service"
										className="px-4 py-2 text-xl font-normal text-gray-800 hover:bg-gray-100 rounded-md transition-colors whitespace-nowrap no-underline hover:underline"
									>
										Request Scanning
									</RouterLink>
								))}
							{canSeeNavGroup("utilities", userAccess) && (
								<button
									ref={utilitiesRefX}
									onMouseEnter={() => openMenu("utilities")}
									onMouseLeave={scheduleClose}
									className="px-4 py-2 text-xl font-normal text-gray-800 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
								>
									{NAV_GROUPS.utilities.label}
								</button>
							)}

							{canSeeNavGroup("admin", userAccess) && (
								<button
									ref={adminRefX}
									onMouseEnter={() => openMenu("admin")}
									onMouseLeave={scheduleClose}
									className="px-4 py-2 text-xl font-normal text-gray-800 hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
								>
									{NAV_GROUPS.admin.label}
								</button>
							)}
							{(userAccess.length === 0 ||
								!userAccess ||
								(userAccess.length === 1 && access === "STD")) && (
								<Box
									sx={{
										display: "flex",
										flexDirection: "column",
										justifyContent: "center",
									}}
								>
									<GiveRoleForm
										buttonVariant="outlined"
										buttonTitle="Need to Submit a Request for Service?"
										buttonWidth="300px"
									/>
								</Box>
							)}
						</div>

						<div className="flex  items-center justify-end h-full gap-1 pr-0 md:pr-10">
							{adminUser && (
								<RouterLink
									to="/admin/scanning-calendar"
									className="p-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
									title="Scanning Calendar"
								>
									<CalendarMonthIcon sx={{ width: 28, height: 28 }} color="primary" />
								</RouterLink>
							)}
							<NotificationMenu resultsEmailNotificationData={resultsEmailNotificationData} />
						</div>
					</div>
				</Toolbar>

				<Popper
					open={open && Boolean(anchorEl)}
					anchorEl={anchorEl}
					placement="bottom-start"
					transition
					sx={{
						borderRadius: "0px 0px 5px 5px",
					}}
					style={{ zIndex: 1300 }} // above AppBar
				>
					{({ TransitionProps }) => (
						<Grow {...TransitionProps} style={{ transformOrigin: "0 0 0" }}>
							<Paper
								elevation={3}
								onMouseEnter={handlePopperEnter}
								onMouseLeave={handlePopperLeave}
								className="p-2 bg-white border border-gray-200 rounded-b-lg shadow-lg overflow-y-auto"
								style={{
									maxHeight: maxHeight,
									minWidth: `${widthByKey[mouseOverButtonState] || 400}px`,
								}}
							>
								{mouseOverButtonState === "reporting" && (
									<Box sx={{ display: "flex", flexDirection: "column" }}>
										{getNavGroupRoutes("reporting", userAccess).map((route) => (
											<NavButton key={route.path} navTo={route.label} link={route.path} />
										))}
									</Box>
								)}

								{mouseOverButtonState === "SPOT" && (
									<Box sx={{ display: "flex", flexDirection: "column" }}>
										{getNavGroupRoutes("spot", userAccess).map((route) => (
											<NavButton key={route.path} navTo={route.label} link={route.path} />
										))}
										{getNavGroupExternalLinks("spot", userAccess).map((link) => (
											<a
												key={link.url}
												className={`px-2 rounded-md text-primary font-semibold hover:underline mt-1 `}
												href={link.url}
												target="_blank"
												rel="noopener noreferrer"
											>
												{link.label} <LaunchIcon />
											</a>
										))}
									</Box>
								)}

								{mouseOverButtonState === "exam" && (
									<Box sx={{ display: "flex", flexDirection: "column" }}>
										{getNavGroupRoutes("exam", userAccess).map((route) => (
											<NavButton key={route.path} navTo={route.label} link={route.path} />
										))}
									</Box>
								)}

								{mouseOverButtonState === "utilities" && (
									<Box sx={{ display: "flex", flexDirection: "column" }}>
										{getNavGroupRoutes("utilities", userAccess).map((route) => (
											<NavButton key={route.path} navTo={route.label} link={route.path} />
										))}
									</Box>
								)}

								{mouseOverButtonState === "admin" && (
									<Box sx={{ display: "flex", flexDirection: "column" }}>
										{getNavGroupRoutes("admin", userAccess).map((route) => (
											<NavButton key={route.path} navTo={route.label} link={route.path} />
										))}
									</Box>
								)}
							</Paper>
						</Grow>
					)}
				</Popper>
			</AppBar>
		);
	}
}

export default AppBarNav;
